/**
 * useTemplateSwitch Hook
 *
 * Business logic for switching note templates with confirmation dialog.
 *
 * Flow:
 * 1. User selects template from dropdown
 * 2. Confirmation dialog appears
 * 3. User confirms → Regenerate note with new template
 * 4. Navigate to status page to watch regeneration
 *
 * Analytics:
 * - Tracks template selection
 * - Tracks confirmation/cancellation
 * - Tracks regeneration success/failure
 */

import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { type TemplatePickerItem } from '@components/Pickers/TemplatePicker';
import { useAccessToken } from '@features/user';
import { useEventTracking } from '@services/statsig';
import { useSnackbar } from 'notistack';

import APIService from '../../../services/APIService';
import { getNoteRoute } from '../../../utils/noteRouting';

export interface UseTemplateSwitchParams {
  /**
   * Current note ID
   */
  noteId?: string;

  /**
   * Current template ID
   */
  currentTemplateId?: string;

  /**
   * Current template name
   */
  currentTemplateName?: string;

  /**
   * Encounter ID for analytics
   */
  encounterId?: string;
}

export interface UseTemplateSwitchReturn {
  /**
   * Selected template (triggers confirmation dialog when set)
   */
  selectedTemplate: TemplatePickerItem | null;

  /**
   * Whether regeneration is in progress
   */
  isRegenerating: boolean;

  /**
   * Whether confirmation dialog should be shown
   */
  showConfirmDialog: boolean;

  /**
   * Current template info for display
   */
  currentTemplate: {
    id?: string;
    name?: string;
  };

  /**
   * Handle template selection from dropdown
   */
  handleTemplateSelect: (template: TemplatePickerItem) => void;

  /**
   * Handle user confirming regeneration
   */
  handleConfirmRegenerate: () => Promise<void>;

  /**
   * Handle user cancelling regeneration
   */
  handleCancelRegenerate: () => void;
}

/**
 * Hook for managing template switching with confirmation
 */
export const useTemplateSwitch = ({
  noteId,
  currentTemplateId,
  currentTemplateName,
  encounterId,
}: UseTemplateSwitchParams): UseTemplateSwitchReturn => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { getAccessToken } = useAccessToken();
  const { note: noteMetrics } = useEventTracking();

  const [selectedTemplate, setSelectedTemplate] = useState<TemplatePickerItem | null>(null);
  const [isRegenerating, setIsRegenerating] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);

  /**
   * Reset state when noteId changes
   * This handles the case where user navigates to a new note after regeneration
   * (component doesn't remount, so state would otherwise persist)
   */
  useEffect(() => {
    setSelectedTemplate(null);
    setShowConfirmDialog(false);
    setIsRegenerating(false);
  }, [noteId]);

  /**
   * Handle template selection from dropdown
   * Shows confirmation dialog
   */
  const handleTemplateSelect = (template: TemplatePickerItem) => {
    // Track template selection
    if (noteId) {
      noteMetrics.templateSelected({
        note_id: noteId,
        current_template_id: currentTemplateId,
        new_template_id: template.template_id,
        new_template_name: template.display_name,
        encounter_id: encounterId,
      });
    }

    setSelectedTemplate(template);
    setShowConfirmDialog(true);
  };

  /**
   * Handle user confirming regeneration
   * Calls API and navigates to status page
   */
  const handleConfirmRegenerate = async () => {
    if (!selectedTemplate || !noteId) {
      console.error('Cannot regenerate: missing template or note ID');
      return;
    }

    // Track confirmation
    if (noteId) {
      noteMetrics.templateRegenerateConfirmed(noteId, {
        current_template_id: currentTemplateId,
        new_template_id: selectedTemplate.template_id,
        new_template_name: selectedTemplate.display_name,
        encounter_id: encounterId,
      });
    }

    setIsRegenerating(true);

    try {
      const accessToken = await getAccessToken();

      if (!accessToken) {
        throw new Error('Authentication failed');
      }

      const response = await APIService.makeAPIPostRequest({
        requestString: '/noteProcessingSession/retryNoteProcessingWithNewTemplate',
        accessToken: accessToken,
        body: {
          note_id: noteId,
          template_id: selectedTemplate.template_id,
        },
      });

      if (response.ok) {
        const { note_id: newNoteId, thread_id } = response.value;

        // Reset modal state before navigation to ensure clean transition
        // This is important because React Router reuses the component when
        // navigating between /note/A and /note/B, so state persists
        setSelectedTemplate(null);
        setShowConfirmDialog(false);
        setIsRegenerating(false);

        if (thread_id) {
          // V2 flow - use hard navigation to force complete component remount
          // Pass encounter_id via query param so SSE can start immediately
          // (the note data from GET /notes/{id} might not have thread_id yet)
          enqueueSnackbar('Regenerating note with new template...', {
            variant: 'info',
          });
          const encId = response.value.encounter_id;
          window.location.href = `/note/${newNoteId}?generating=true&eid=${encId}`;
        } else {
          // V1 flow
          enqueueSnackbar('Note regeneration started successfully!', {
            variant: 'success',
          });
          navigate(getNoteRoute({ note_id: newNoteId }));
        }
      } else {
        throw new Error('Failed to regenerate note');
      }
    } catch (error) {
      console.error('Error regenerating note:', error);

      enqueueSnackbar(
        error instanceof Error ? error.message : 'Failed to regenerate note. Please try again.',
        {
          variant: 'error',
        },
      );

      setIsRegenerating(false);
    }
  };

  /**
   * Handle user cancelling regeneration
   * Cleans up state and closes dialog
   */
  const handleCancelRegenerate = () => {
    // Track cancellation
    if (noteId && selectedTemplate) {
      noteMetrics.templateRegenerateCancelled(noteId, {
        current_template_id: currentTemplateId,
        new_template_id: selectedTemplate.template_id,
        new_template_name: selectedTemplate.display_name,
        encounter_id: encounterId,
      });
    }

    setSelectedTemplate(null);
    setShowConfirmDialog(false);
    setIsRegenerating(false);
  };

  return {
    selectedTemplate,
    isRegenerating,
    showConfirmDialog,
    currentTemplate: {
      id: currentTemplateId,
      name: currentTemplateName,
    },
    handleTemplateSelect,
    handleConfirmRegenerate,
    handleCancelRegenerate,
  };
};
